//////////////////////////////////////////////////////////////////////////////
//
//  사용자 추가 팝업
//
//  작성자: MediInStep Team
//  생성일: 2025-01-XX
//
//  설명: 사용자 등록을 위한 팝업 다이얼로그
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import Chip from "@mui/material/Chip"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Card from "@mui/material/Card"
import Switch from "@mui/material/Switch"
import FormControlLabel from "@mui/material/FormControlLabel"
import Slider from "@mui/material/Slider"
import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import { request } from "app/network"
import Swal from "sweetalert2"
import { useMaterialUIController, MaterialUI } from "context"
import { useTheme, ThemeProvider } from "@mui/material/styles"
import { createTheme } from "@mui/material/styles"
import QRCode from "qrcode"

interface AddUserDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  darkMode?: boolean
  theme?: any // Material-UI theme
}

interface AddUserDialogState {
  id: string
  name: string
  email: string
  mobile: string
  password: string
  role: string
  tenantRole: string
  loginType: string
  enable: boolean
  isMasterUser: boolean // 생성할 사용자의 마스터 여부 (전화번호 중복 허용)
  tenantSeqId: number | null // 선택된 테넌트 ID
  currentUserIsMaster: boolean // 현재 로그인한 사용자가 마스터인지 여부
  currentUserTenantSeqId: number | null // 현재 로그인한 사용자의 테넌트 ID
  loading: boolean
  tenantRoles: any[]
  loadingTenantRoles: boolean
  tenants: any[] // 테넌트 목록
  loadingTenants: boolean
  loginTypes: any[]
  loadingLoginTypes: boolean
  generatedPin: string // PIN 생성 시 저장
  qrToken: string | null // QR 토큰 (등록 완료 후 서버에서 받은 토큰)
  qrExpiresInMinutes: number | null // QR 만료시간 (분 단위, null이면 만료 없음)
  qrExpiresNever: boolean // QR 만료 없음 여부
  darkMode: boolean // 다크 모드 상태 저장
  // Policy selections
  stepPolicySeqIds: number[] // 걸음수 포인트 적립 정책 (다중 선택)
  videoPolicySeqIds: number[] // 영상시청 포인트 적립 정책 (다중 선택)
  pointLimitPolicySeqIds: number[] // 포인트 한도 정책 (다중 선택)
  challengePolicySeqIds: number[] // 챌린지 정책 (다중 선택)
  usePolicySeqIds: number[] // 포인트 사용 정책 (다중 선택)

  stepPolicies: any[] // 걸음수 정책 목록
  videoPolicies: any[] // 영상시청 정책 목록
  pointLimitPolicies: any[] // 포인트 한도 정책 목록
  challengePolicies: any[] // 챌린지 정책 목록
  usePolicies: any[] // 사용 정책 목록
  allDefaultPolicies: any[] // 모든 기본 정책 목록 (자동 할당 표시용)

  loadingPolicies: boolean // 정책 로딩 상태
  // Surgery Site State
  liveSurgerySites: Array<{ seqId: number, siteName: string, date: Date | null }>
  availableSurgerySites: any[]
  selectedSurgerySite: any | null
  selectedSurgeryDate: Date | null
  loadingSurgerySites: boolean
  // Interest Areas (Body Parts)
  interestBodyPartIds: number[]
  availableBodyParts: any[]
  interestAreaCategories: any[]
}

class AddUserDialogClass extends Component<AddUserDialogProps, AddUserDialogState> {
  static contextType = MaterialUI

  constructor(props: AddUserDialogProps) {
    super(props)
    // props에서 darkMode 초기값 가져오기 (우선순위: props > context)
    const initialDarkMode = props.darkMode ?? false

    this.state = {
      id: "",
      name: "",
      email: "",
      mobile: "",
      password: "",
      role: "",
      tenantRole: "",
      loginType: "",
      enable: true,
      isMasterUser: false, // 마스터 사용자 기본값 false
      tenantSeqId: null, // 테넌트 미선택
      currentUserIsMaster: false, // 현재 사용자 마스터 여부 (로드 후 업데이트)
      currentUserTenantSeqId: null, // 현재 로그인한 사용자의 테넌트 ID
      loading: false,
      tenantRoles: [],
      loadingTenantRoles: false,
      tenants: [], // 테넌트 목록
      loadingTenants: false,
      loginTypes: [],
      loadingLoginTypes: false,
      generatedPin: "", // PIN 생성 시 저장
      qrToken: null, // QR 토큰 초기화
      qrExpiresInMinutes: null, // QR 만료시간 기본값 null (만료 없음)
      qrExpiresNever: true, // QR 만료 없음 기본값 true
      darkMode: initialDarkMode, // props에서 가져온 초기값 사용
      // Policy selections
      stepPolicySeqIds: [],
      videoPolicySeqIds: [],
      pointLimitPolicySeqIds: [],
      usePolicySeqIds: [],

      stepPolicies: [],
      videoPolicies: [],
      pointLimitPolicies: [],
      usePolicies: [],
      allDefaultPolicies: [],
      challengePolicies: [],

      challengePolicySeqIds: [],

      loadingPolicies: false,
      // Surgery Site State
      liveSurgerySites: [],
      availableSurgerySites: [],
      selectedSurgerySite: null,
      selectedSurgeryDate: null,
      loadingSurgerySites: false,
      // Interest Areas
      interestBodyPartIds: [],
      availableBodyParts: [],
      interestAreaCategories: [],
    }
  }

  componentDidMount() {
    // props에서 darkMode 가져와서 state에 저장 (우선순위: props > context)
    const context = this.context as any
    const initialDarkMode = this.props.darkMode ?? context?.darkMode ?? false
    this.setState({ darkMode: initialDarkMode }, () => {
      // 다크 모드 설정 후 강제 리렌더링을 위해 상태 업데이트
      if (this.props.open) {
        this.loadTenantRoles()
        this.loadLoginTypes()
      }
    })
  }

  componentDidUpdate(prevProps: AddUserDialogProps, prevState: AddUserDialogState) {
    // props에서 darkMode 변경 감지 (우선순위: props > context)
    const context = this.context as any
    const currentDarkMode = this.props.darkMode ?? context?.darkMode ?? false
    if (currentDarkMode !== this.state.darkMode) {
      this.setState({ darkMode: currentDarkMode })
    }
    // 다이얼로그가 열릴 때마다 테넌트 역할 및 로그인 타입 로드
    if (this.props.open && !prevProps.open) {
      // 다이얼로그가 열릴 때 필드 초기화 및 다크 모드 재설정
      this.setState(
        {
          id: "",
          password: "",
          qrToken: null, // QR 토큰 초기화
          qrExpiresInMinutes: null, // QR 만료시간 초기화 (만료 없음)
          qrExpiresNever: false, // QR 만료 없음 초기화
          darkMode: currentDarkMode,
        },
        () => {
          this.loadTenantRoles()
          this.loadLoginTypes()
          this.loadPolicies()
          this.loadTenants() // 테넌트 목록 로드
          this.loadCurrentUser() // 현재 사용자 정보 로드
          this.loadSurgerySites() // 수술 부위 목록 로드
        }
      )
    }

    // 로그인 타입이 PIN으로 변경되면 랜덤 4자리 PIN 생성
    if ((this.state.loginType === "PIN" || this.state.loginType === "LOGIN_PIN") &&
      (prevState.loginType !== "PIN" && prevState.loginType !== "LOGIN_PIN")) {
      this.generatePin()
    }

    // 로그인 타입이 PIN이 아닌 다른 것으로 변경되면 PIN 초기화
    if ((this.state.loginType !== "PIN" && this.state.loginType !== "LOGIN_PIN") &&
      (prevState.loginType === "PIN" || prevState.loginType === "LOGIN_PIN")) {
      this.setState({ generatedPin: "" })
    }
  }

  loadTenantRoles = async () => {
    try {
      this.setState({ loadingTenantRoles: true })
      console.log("테넌트 역할 목록 조회 시작...")
      const response = await request("CommonCode.getByGroup", {
        paramz: {},
        pathParams: { groupCode: "TENANT_ROLE" },
      })

      console.log("테넌트 역할 목록 조회 응답:", response)

      if (response && response.data) {
        const tenantRoles = Array.isArray(response.data) ? response.data : []
        console.log("테넌트 역할 목록:", tenantRoles)
        this.setState({
          tenantRoles,
          tenantRole: tenantRoles.length > 0 ? tenantRoles[0].code : "",
        })
      }
    } catch (error: any) {
      console.error("테넌트 역할 목록 조회 실패:", error)
      console.error("에러 상세:", error.response?.data || error.message)
      const { theme, darkMode: propsDarkMode } = this.props
      const { darkMode: stateDarkMode } = this.state
      const context = this.context as any
      const finalDarkMode = propsDarkMode ?? stateDarkMode ?? context?.darkMode ?? false
      Swal.fire({
        icon: "error",
        title: "테넌트 역할 조회 실패",
        text: error.response?.data?.message || "테넌트 역할 목록을 불러올 수 없습니다.",
        confirmButtonText: "확인",
        background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
        color: finalDarkMode ? "#ffffff" : "#000000",
        confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
        customClass: {
          container: "swal2-container-high-zindex",
        },
        didOpen: () => {
          // z-index를 높게 설정하여 Dialog 위에 표시
          const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
          if (swalContainer) {
            swalContainer.style.zIndex = "1400"
          }
        },
      })
      this.setState({ tenantRoles: [] })
    } finally {
      this.setState({ loadingTenantRoles: false })
    }
  }

  loadLoginTypes = async () => {
    try {
      this.setState({ loadingLoginTypes: true })
      console.log("로그인 타입 목록 조회 시작...")
      const response = await request("CommonCode.getByGroup", {
        paramz: {},
        pathParams: { groupCode: "LOGIN_TYPE" },
      })

      console.log("로그인 타입 목록 조회 응답:", response)

      if (response && response.data) {
        const allLoginTypes = Array.isArray(response.data) ? response.data : []
        // isActive가 true이고 ADMIN이 아닌 것만 필터링 (QR, PIN만)
        const loginTypes = allLoginTypes.filter((type: any) => type.isActive === true && type.code !== "ADMIN")
        console.log("로그인 타입 목록 (필터링 후):", loginTypes)
        const defaultLoginType = loginTypes.length > 0 ? loginTypes[0].code : ""
        this.setState({
          loginTypes,
          loginType: defaultLoginType,
        })

        // 기본 로그인 타입이 PIN이면 자동으로 PIN 생성
        if (defaultLoginType === "PIN" || defaultLoginType === "LOGIN_PIN") {
          this.generatePin()
        }
      }
    } catch (error: any) {
      console.error("로그인 타입 목록 조회 실패:", error)
      console.error("에러 상세:", error.response?.data || error.message)
      this.setState({ loginTypes: [] })
    } finally {
      this.setState({ loadingLoginTypes: false })
    }
  }

  loadPolicies = async () => {
    try {
      this.setState({ loadingPolicies: true })

      // Fetch policy types in parallel
      const [allEarnResponse, useResponse, pointLimitResponse, bodyPartsResponse, categoriesResponse] = await Promise.all([
        request("PointPolicy.list", { paramz: {} }),
        request("PointUsePolicy.list", { paramz: {} }),
        request("PointLimitPolicy.list", { paramz: {} }),
        request("BodyPart.getAll", { paramz: {} }),
        request("CommonCode.getByGroup", { pathParams: { groupCode: "INTEREST_AREA_CATEGORY" } }),
      ])

      // Filter earning policies by type
      const allEarnPolicies = allEarnResponse?.data || []

      const challengePolicies = allEarnPolicies.filter((p: any) =>
        p.conditionType === "MONTHLY_GOAL" ||
        p.conditionType === "MONTHLY_CHALLENGE" ||
        p.policyType?.includes("CHALLENGE")
      )

      // Helper to identify video policies
      const isVideoPolicy = (p: any) =>
        !challengePolicies.some((cp: any) => cp.seqId === p.seqId) &&
        (
          p.policyType?.includes("VIDEO") ||
          p.conditionType === "VIDEO_COMPLETE" ||
          p.conditionType === "DAILY_VIDEO_COUNT" ||
          p.policyName?.includes("영상")
        );

      const videoPolicies = allEarnPolicies.filter((p: any) => isVideoPolicy(p))

      const stepPolicies = allEarnPolicies.filter((p: any) =>
        !isVideoPolicy(p) &&
        !challengePolicies.some((cp: any) => cp.seqId === p.seqId) &&
        (p.conditionType === "DAILY_GOAL" || p.conditionType === "STEP_COUNT" || p.policyType?.includes("STEP"))
      )

      const usePolicies = useResponse?.data || []
      const pointLimitPolicies = pointLimitResponse?.data || []

      // Auto-select default policies
      const defaultStepPolicySeqIds = stepPolicies
        .filter((p: any) => p.isDefaultPolicy === true)
        .map((p: any) => p.seqId)

      const defaultVideoPolicySeqIds = videoPolicies
        .filter((p: any) => p.isDefaultPolicy === true)
        .map((p: any) => p.seqId)

      const defaultChallengePolicySeqIds = challengePolicies
        .filter((p: any) => p.isDefaultPolicy === true)
        .map((p: any) => p.seqId)

      const defaultPointLimitPolicySeqIds = pointLimitPolicies
        .filter((p: any) => p.isDefault === true)
        .map((p: any) => p.seqId)

      const defaultUsePolicySeqIds = usePolicies
        .filter((p: any) => p.isDefault === true)
        .map((p: any) => p.seqId)


      this.setState({
        stepPolicies,
        videoPolicies,
        challengePolicies,
        usePolicies,
        pointLimitPolicies,
        // Auto-select default policies
        stepPolicySeqIds: defaultStepPolicySeqIds,
        videoPolicySeqIds: defaultVideoPolicySeqIds,
        challengePolicySeqIds: defaultChallengePolicySeqIds,
        pointLimitPolicySeqIds: defaultPointLimitPolicySeqIds,
        usePolicySeqIds: defaultUsePolicySeqIds,
        interestAreaCategories: categoriesResponse?.data || [],
        availableBodyParts: bodyPartsResponse?.data || [],
        allDefaultPolicies: [
          ...allEarnPolicies.filter((p: any) => p.isDefaultPolicy === true),
          ...usePolicies.filter((p: any) => p.isDefault === true),
          ...pointLimitPolicies.filter((p: any) => p.isDefault === true),
        ],
      })
    } catch (error: any) {
      console.error("정책 목록 조회 실패:", error)
      this.setState({
        stepPolicies: [],
        videoPolicies: [],
        pointLimitPolicies: [],
        usePolicies: [],
        allDefaultPolicies: [],

      })
    } finally {
      this.setState({ loadingPolicies: false })
    }
  }

  loadTenants = async () => {
    try {
      this.setState({ loadingTenants: true })
      console.log("테넌트 목록 조회 시작...")
      const response = await request("Tenant.list", { paramz: {} })

      console.log("테넌트 목록 조회 응답:", response)

      if (response && response.data) {
        const tenants = Array.isArray(response.data) ? response.data : []
        console.log("테넌트 목록:", tenants)
        this.setState({
          tenants,
          tenantSeqId: tenants.length > 0 ? tenants[0].seqId : null, // 첫 번째 테넌트 자동 선택
        })
      }
    } catch (error: any) {
      console.error("테넌트 목록 조회 실패:", error)
      this.setState({ tenants: [] })
    } finally {
      this.setState({ loadingTenants: false })
    }
  }

  loadSurgerySites = async () => {
    try {
      this.setState({ loadingSurgerySites: true })
      const response = await request("SurgerySite.pagedList", {
        paramz: { PageNumber: 1, PageSize: 100 } // Fetch enough for dropdown
      })
      if (response && response.data && response.data.items) {
        this.setState({ availableSurgerySites: response.data.items })
      }
    } catch (error) {
      console.error("수술 부위 목록 조회 실패:", error)
    } finally {
      this.setState({ loadingSurgerySites: false })
    }
  }

  handleAddSurgerySite = () => {
    const { selectedSurgerySite, selectedSurgeryDate, liveSurgerySites } = this.state
    if (!selectedSurgerySite) return

    // Check duplicate - REMOVED to allow multiples
    // if (liveSurgerySites.some(s => s.seqId === selectedSurgerySite.seqId)) {
    //   alert("이미 추가된 수술 부위입니다.")
    //   return
    // }

    this.setState({
      liveSurgerySites: [
        ...liveSurgerySites,
        {
          seqId: selectedSurgerySite.seqId,
          siteName: selectedSurgerySite.siteName,
          date: selectedSurgeryDate
        }
      ],
      selectedSurgerySite: null,
      selectedSurgeryDate: null
    })
  }

  handleRemoveSurgerySite = (index: number) => {
    this.setState({
      liveSurgerySites: this.state.liveSurgerySites.filter((_, i) => i !== index)
    })
  }

  loadCurrentUser = async () => {
    try {
      // JWT 토큰에서 직접 읽기 (암호화된 토큰을 복호화)
      const { getStoredAccessToken } = await import("Shared/Utils/CredentialStorage")
      const token = getStoredAccessToken()

      if (!token) {
        this.setState({ currentUserIsMaster: false })
        return
      }

      // JWT 토큰 디코딩 (Base64)
      try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
        const payload = JSON.parse(jsonPayload)

        // isMasterUser claim 확인
        const isMaster = payload.isMasterUser === "true" || payload.isMasterUser === true

        // 현재 사용자의 테넌트 ID 저장
        const currentTenantSeqId = payload.tenantSeqId ? parseInt(payload.tenantSeqId) : null

        this.setState({
          currentUserIsMaster: isMaster,
          currentUserTenantSeqId: currentTenantSeqId
        })
      } catch (decodeError) {
        console.error("JWT 토큰 디코딩 실패:", decodeError)
        this.setState({ currentUserIsMaster: false })
      }
    } catch (error: any) {
      console.error("마스터 사용자 정보 확인 실패:", error)
      this.setState({ currentUserIsMaster: false })
    }
  }

  handleChange = (field: keyof AddUserDialogState, value: any) => {
    this.setState({ [field]: value } as any)
  }

  // 랜덤 4자리 PIN 생성
  generatePin = () => {
    const pin = Math.floor(1000 + Math.random() * 9000).toString() // 1000~9999 사이의 4자리 숫자
    this.setState({ generatedPin: pin })
  }

  handleSubmit = async () => {
    const { id, name, email, mobile, password, role, tenantRole, loginType, enable } = this.state
    const { theme, darkMode: propsDarkMode } = this.props
    const { darkMode: stateDarkMode } = this.state
    const context = this.context as any
    const finalDarkMode = propsDarkMode ?? stateDarkMode ?? context?.darkMode ?? false

    // 유효성 검사
    if (tenantRole !== "USER") {
      // USER가 아닌 경우: ID와 비밀번호 필수
      if (!id.trim()) {
        Swal.fire({
          icon: "error",
          title: "입력 오류",
          text: "사용자 ID를 입력해주세요.",
          confirmButtonText: "확인",
          background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
          color: finalDarkMode ? "#ffffff" : "#000000",
          confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
          customClass: {
            container: "swal2-container-high-zindex",
          },
          didOpen: () => {
            // z-index를 높게 설정하여 Dialog 위에 표시
            const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
            if (swalContainer) {
              swalContainer.style.zIndex = "1400"
            }
          },
        })
        return
      }

      if (!password.trim()) {
        Swal.fire({
          icon: "error",
          title: "입력 오류",
          text: "비밀번호를 입력해주세요.",
          confirmButtonText: "확인",
          background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
          color: finalDarkMode ? "#ffffff" : "#000000",
          confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
          customClass: {
            container: "swal2-container-high-zindex",
          },
          didOpen: () => {
            // z-index를 높게 설정하여 Dialog 위에 표시
            const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
            if (swalContainer) {
              swalContainer.style.zIndex = "1400"
            }
          },
        })
        return
      }
    } else {
      // USER인 경우: 로그인 타입 필수, 이메일 또는 전화번호 중 하나 필수
      if (!loginType.trim()) {
        Swal.fire({
          icon: "error",
          title: "입력 오류",
          text: "로그인 타입을 선택해주세요.",
          confirmButtonText: "확인",
          background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
          color: finalDarkMode ? "#ffffff" : "#000000",
          confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
          customClass: {
            container: "swal2-container-high-zindex",
          },
          didOpen: () => {
            // z-index를 높게 설정하여 Dialog 위에 표시
            const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
            if (swalContainer) {
              swalContainer.style.zIndex = "1400"
            }
          },
        })
        return
      }

      if (!email.trim() && !mobile.trim()) {
        Swal.fire({
          icon: "error",
          title: "입력 오류",
          text: "이메일 또는 전화번호 중 하나는 필수입니다.",
          confirmButtonText: "확인",
          background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
          color: finalDarkMode ? "#ffffff" : "#000000",
          confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
          customClass: {
            container: "swal2-container-high-zindex",
          },
          didOpen: () => {
            // z-index를 높게 설정하여 Dialog 위에 표시
            const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
            if (swalContainer) {
              swalContainer.style.zIndex = "1400"
            }
          },
        })
        return
      }
    }


    try {
      this.setState({ loading: true })

      const param = {
        Id: tenantRole === "USER" ? "" : id, // USER일 때는 빈 문자열 (서버에서 이메일/전화번호로 대체)
        Name: name || undefined,
        Email: email || undefined,
        Mobile: mobile || undefined,
        Password: tenantRole === "USER" ? undefined : password,
        Role: tenantRole || role || undefined,
        LoginType: tenantRole === "USER" ?
          (loginType === "LOGIN_QR" ? "QR" : (loginType === "LOGIN_PIN" ? "PIN" : loginType))
          : undefined,
        PinCode: tenantRole === "USER" && (loginType === "PIN" || loginType === "LOGIN_PIN") ? this.state.generatedPin : undefined, // PIN이 선택된 경우 생성된 PIN 전송
        QrExpiresInMinutes:
          tenantRole === "USER" && (loginType === "QR" || loginType === "LOGIN_QR")
            ? this.state.qrExpiresNever
              ? 0
              : null // 기본값: 만료 없음
            : undefined,
        Enable: enable,
        IsMasterUser: this.state.isMasterUser, // 마스터 사용자 여부
        // 마스터 사용자: 선택한 테넌트 ID, 일반 사용자: 로그인한 사용자의 테넌트 ID
        TenantSeqId: this.state.currentUserIsMaster
          ? (this.state.tenantSeqId || undefined)
          : (this.state.currentUserTenantSeqId || undefined),
        // Policy selections
        StepPolicySeqIds: this.state.stepPolicySeqIds,
        VideoPolicySeqIds: this.state.videoPolicySeqIds,
        PointLimitPolicySeqIds: this.state.pointLimitPolicySeqIds,
        ChallengePolicySeqIds: this.state.challengePolicySeqIds,
        UsePolicySeqIds: this.state.usePolicySeqIds,

        SurgerySites: this.state.liveSurgerySites.map(s => ({
          SurgerySiteSeqId: s.seqId,
          SurgeryDate: s.date
        })),
        InterestBodyPartIds: this.state.interestBodyPartIds,
      }

      const response = await request("User.create", { paramz: param })

      // 디버깅: 응답 확인
      console.log("User.create 응답:", response)
      console.log("response.data:", response?.data)
      console.log("response.data.qrToken:", response?.data?.qrToken)
      console.log("tenantRole:", tenantRole, "loginType:", loginType)

      // QR 로그인 타입이고 응답에 QR 토큰이 있으면 저장
      let qrTokenValue: string | null = null
      // Check for both QR and LOGIN_QR
      const isQrType = loginType === "QR" || loginType === "LOGIN_QR";

      if (tenantRole === "USER" && isQrType && response?.data?.qrToken) {
        qrTokenValue = response.data.qrToken
        console.log("QR 토큰 발견:", qrTokenValue?.substring(0, 50) + "...")
        this.setState({ qrToken: qrTokenValue })
      } else {
        console.log("QR 토큰 조건 불일치:", {
          isUser: tenantRole === "USER",
          isQR: isQrType,
          hasQrToken: !!response?.data?.qrToken,
          loginType: loginType
        })
      }

      // QR 토큰이 있으면 QR 코드를 보여주는 커스텀 알림 표시
      if (qrTokenValue) {
        try {
          // QR 코드 SVG 생성
          const qrCodeSvg = await QRCode.toString(qrTokenValue, {
            type: "svg",
            width: 200,
            margin: 2,
            color: {
              dark: finalDarkMode ? "#ffffff" : "#000000",
              light: finalDarkMode ? "#1a1a1a" : "#ffffff",
            },
          })

          Swal.fire({
            icon: "success",
            title: "등록 완료",
            html: `
              <div style="text-align: center;">
                <p style="margin-bottom: 20px; color: ${finalDarkMode ? "#ffffff" : "#000000"};">사용자가 성공적으로 등록되었습니다.</p>
                <div style="display: flex; justify-content: center; margin: 20px 0;">
                  ${qrCodeSvg}
                </div>
                <p style="margin-top: 20px; font-size: 0.875rem; color: ${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "#666"};">QR 코드를 스캔하여 로그인할 수 있습니다.</p>
              </div>
            `,
            confirmButtonText: "확인",
            background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
            color: finalDarkMode ? "#ffffff" : "#000000",
            confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
            customClass: {
              container: "swal2-container-high-zindex",
            },
            didOpen: () => {
              // z-index를 높게 설정하여 Dialog 위에 표시
              const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
              if (swalContainer) {
                swalContainer.style.zIndex = "1400"
              }
            },
          }).then(() => {
            // 알림 닫힌 후 폼 초기화 및 다이얼로그 닫기
            this.setState({
              id: "",
              name: "",
              email: "",
              mobile: "",
              password: "",
              role: "",
              tenantRole: this.state.tenantRoles.length > 0 ? this.state.tenantRoles[0].code : "",
              loginType: this.state.loginTypes.length > 0 ? this.state.loginTypes[0].code : "",
              enable: true,
              generatedPin: "",
              qrToken: null,
              qrExpiresInMinutes: null,
              qrExpiresNever: false,
            })
            this.props.onSuccess()
            this.props.onClose()
          })
        } catch (qrError) {
          console.error("QR 코드 생성 실패:", qrError)
          // QR 코드 생성 실패 시 일반 성공 알림
          Swal.fire({
            icon: "success",
            title: "등록 완료",
            text: "사용자가 성공적으로 등록되었습니다.",
            confirmButtonText: "확인",
            background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
            color: finalDarkMode ? "#ffffff" : "#000000",
            confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
            customClass: {
              container: "swal2-container-high-zindex",
            },
            didOpen: () => {
              // z-index를 높게 설정하여 Dialog 위에 표시
              const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
              if (swalContainer) {
                swalContainer.style.zIndex = "1400"
              }
            },
          }).then(() => {
            this.setState({
              id: "",
              name: "",
              email: "",
              mobile: "",
              password: "",
              role: "",
              tenantRole: this.state.tenantRoles.length > 0 ? this.state.tenantRoles[0].code : "",
              loginType: this.state.loginTypes.length > 0 ? this.state.loginTypes[0].code : "",
              enable: true,
              generatedPin: "",
              qrToken: null,
            })
            this.props.onSuccess()
            this.props.onClose()
          })
        }
      } else {
        // QR 토큰이 없으면 일반 성공 알림
        Swal.fire({
          icon: "success",
          title: "등록 완료",
          text: "사용자가 성공적으로 등록되었습니다.",
          confirmButtonText: "확인",
          background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
          color: finalDarkMode ? "#ffffff" : "#000000",
          confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
          customClass: {
            container: "swal2-container-high-zindex",
          },
          didOpen: () => {
            // z-index를 높게 설정하여 Dialog 위에 표시
            const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
            if (swalContainer) {
              swalContainer.style.zIndex = "1400"
            }
          },
        }).then(() => {
          // 폼 초기화
          this.setState({
            id: "",
            name: "",
            email: "",
            mobile: "",
            password: "",
            role: "",
            tenantRole: this.state.tenantRoles.length > 0 ? this.state.tenantRoles[0].code : "",
            loginType: this.state.loginTypes.length > 0 ? this.state.loginTypes[0].code : "",
            enable: true,
            generatedPin: "",
            qrToken: null,
            qrExpiresInMinutes: null,
            qrExpiresNever: false,
            liveSurgerySites: [],
            selectedSurgerySite: null,
            selectedSurgeryDate: null,
          })
          this.props.onSuccess()
          this.props.onClose()
        })
      }
    } catch (error: any) {
      console.error("사용자 등록 실패:", error)

      let errorMessage = "사용자 등록 중 오류가 발생했습니다."
      if (error.response?.status === 409) {
        errorMessage = error.response?.data?.message || "이미 존재하는 사용자 ID입니다."
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }

      Swal.fire({
        icon: "error",
        title: "등록 실패",
        text: errorMessage,
        confirmButtonText: "확인",
        background: finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
        color: finalDarkMode ? "#ffffff" : "#000000",
        confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
        customClass: {
          container: "swal2-container-high-zindex",
        },
        didOpen: () => {
          // z-index를 높게 설정하여 Dialog 위에 표시
          const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement
          if (swalContainer) {
            swalContainer.style.zIndex = "1400"
          }
        },
      })
    } finally {
      this.setState({ loading: false })
    }
  }

  render() {
    const { open, onClose, theme, darkMode: propsDarkMode } = this.props
    const { id, name, email, mobile, password, role, tenantRole, loginType, enable, loading, tenantRoles, loadingTenantRoles, loginTypes, loadingLoginTypes, darkMode: stateDarkMode } = this.state

    // props의 darkMode를 우선 사용, 없으면 state의 darkMode 사용
    const darkMode = propsDarkMode ?? stateDarkMode ?? false

    // Context에서도 확인 (최종 fallback)
    const context = this.context as any
    const finalDarkMode = darkMode ?? context?.darkMode ?? false

    // 테마에서 배경색 가져오기
    const backgroundColor = finalDarkMode ? theme?.palette?.background?.default || "#1a1a1a" : theme?.palette?.background?.paper || "#ffffff"

    // 입력 필드 배경색 (사용자 ID와 비밀번호용)
    const inputBackgroundColor = finalDarkMode ? (theme?.palette?.mode === "dark" ? theme?.palette?.background?.paper || "#1e1e1e" : theme?.palette?.background?.default || "#1a1a1a") : theme?.palette?.background?.default || "#ffffff"

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: backgroundColor,
          },
        }}>
        <DialogTitle
          sx={{
            backgroundColor: backgroundColor,
            padding: "24px 24px 16px 24px", // 상단 padding 증가
          }}>
          <MDTypography component="span" fontWeight="medium" sx={{ fontSize: "1.25rem" }}>
            사용자 추가
          </MDTypography>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: backgroundColor,
            padding: "16px 24px", // 좌우 padding 증가
          }}>
          <MDBox pt={1} pb={1}>
            <MDBox mb={2}>
              <FormControl fullWidth>
                <InputLabel id="tenant-role-label" sx={{ fontSize: "0.875rem" }}>
                  테넌트 역할
                </InputLabel>
                <Select
                  labelId="tenant-role-label"
                  id="tenant-role-select"
                  value={tenantRole}
                  label="테넌트 역할"
                  onChange={(e: any) => {
                    const newRole = e.target.value;
                    const isUser = newRole === "USER";

                    // 역할 변경 시 로그인 타입 유효성 검사 및 재설정
                    let newLoginType = this.state.loginType;

                    if (isUser) {
                      // USER 역할일 때는 QR, PIN만 허용
                      // Filter checks for LOGIN_QR/LOGIN_PIN (DB codes) OR QR/PIN (Legacy/Safety)
                      const allowedTypes = ["LOGIN_QR", "LOGIN_PIN", "QR", "PIN"];
                      if (!allowedTypes.includes(newLoginType)) {
                        // 현재 선택된 타입이 허용되지 않으면 QR로 기본 설정 (없으면 첫번째 허용 타입)
                        // Assume LOGIN_QR is the newer standard if available
                        newLoginType = "LOGIN_QR";
                      }
                    }

                    this.setState({
                      tenantRole: newRole,
                      loginType: newLoginType
                    }, () => {
                      // PIN으로 변경되었으면 생성, 아니면 초기화 등 추가 로직 실행
                      // Check both legacy "PIN" and new "LOGIN_PIN"
                      const isPinName = newLoginType === "PIN" || newLoginType === "LOGIN_PIN";
                      const wasPinName = this.state.loginType === "PIN" || this.state.loginType === "LOGIN_PIN";

                      if (isPinName && !wasPinName) {
                        this.generatePin();
                      } else if (!isPinName) {
                        this.setState({ generatedPin: "" });
                      }
                    });
                  }}
                  disabled={loadingTenantRoles}
                  sx={{
                    minHeight: "42px",
                    fontSize: "0.875rem",
                    "& .MuiSelect-select": {
                      padding: "10.5px 14px",
                      fontSize: "0.875rem",
                    },
                  }}>
                  {tenantRoles.map((roleItem: any) => (
                    <MenuItem key={roleItem.code} value={roleItem.code} sx={{ fontSize: "0.875rem" }}>
                      {roleItem.name} ({roleItem.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>

            {/* 마스터 사용자 체크박스 */}
            <MDBox mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={this.state.isMasterUser}
                    onChange={(e: any) => this.handleChange("isMasterUser", e.target.checked)}
                    color="info"
                  />
                }
                label={
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    마스터 사용자
                  </MDTypography>
                }
              />
            </MDBox>

            {/* 현재 로그인 사용자가 마스터인 경우 테넌트 선택 가능 */}
            {this.state.currentUserIsMaster && (
              <MDBox mb={2}>
                <FormControl fullWidth>
                  <InputLabel id="tenant-label" sx={{ fontSize: "0.875rem" }}>
                    테넌트
                  </InputLabel>
                  <Select
                    labelId="tenant-label"
                    id="tenant-select"
                    value={this.state.tenantSeqId || ""}
                    label="테넌트"
                    onChange={(e: any) => this.handleChange("tenantSeqId", e.target.value)}
                    disabled={this.state.loadingTenants}
                    sx={{
                      minHeight: "42px",
                      fontSize: "0.875rem",
                      "& .MuiSelect-select": {
                        padding: "10.5px 14px",
                        fontSize: "0.875rem",
                      },
                    }}>
                    {this.state.tenants.map((tenant: any) => (
                      <MenuItem key={tenant.seqId} value={tenant.seqId} sx={{ fontSize: "0.875rem" }}>
                        {tenant.name} (ID: {tenant.seqId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
                  💡 마스터 관리자는 모든 테넌트에 사용자를 생성할 수 있습니다
                </MDTypography>
              </MDBox>
            )}

            {tenantRole !== "USER" ? (
              <>
                <MDBox mb={2}>
                  <MDInput
                    key={`user-id-${finalDarkMode}-${this.props.open}`}
                    label="사용자 ID *"
                    fullWidth
                    value={id || ""}
                    onChange={(e: any) => this.handleChange("id", e.target.value)}
                    required
                    autoComplete="off"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: `${inputBackgroundColor} !important`,
                        "& fieldset": {
                          borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"} !important`,
                        },
                        "&:hover fieldset": {
                          borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"} !important`,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
                      },
                      "& .MuiInputBase-input": {
                        color: `${finalDarkMode ? "#ffffff" : "#000000"} !important`,
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiOutlinedInput-input": {
                        backgroundColor: "transparent !important",
                      },
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    key={`password-${finalDarkMode}-${this.props.open}`}
                    label="비밀번호 *"
                    type="password"
                    fullWidth
                    value={password || ""}
                    onChange={(e: any) => this.handleChange("password", e.target.value)}
                    required
                    autoComplete="new-password"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: `${inputBackgroundColor} !important`,
                        "& fieldset": {
                          borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"} !important`,
                        },
                        "&:hover fieldset": {
                          borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"} !important`,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
                      },
                      "& .MuiInputBase-input": {
                        color: `${finalDarkMode ? "#ffffff" : "#000000"} !important`,
                        backgroundColor: "transparent !important",
                      },
                      "& .MuiOutlinedInput-input": {
                        backgroundColor: "transparent !important",
                      },
                    }}
                  />
                </MDBox>
              </>
            ) : (
              <>
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel id="login-type-label" sx={{ fontSize: "0.875rem" }}>
                      로그인 타입 *
                    </InputLabel>
                    <Select
                      labelId="login-type-label"
                      id="login-type-select"
                      value={loginType}
                      label="로그인 타입 *"
                      onChange={(e: any) => this.handleChange("loginType", e.target.value)}
                      disabled={loadingLoginTypes || loginTypes.length === 0}
                      sx={{
                        minHeight: "42px",
                        fontSize: "0.875rem",
                        "& .MuiSelect-select": {
                          padding: "10.5px 14px",
                          fontSize: "0.875rem",
                        },
                      }}>
                      {loginTypes
                        .filter((loginTypeItem: any) => {
                          // USER 역할일 때는 QR, PIN만 표시
                          if (tenantRole === "USER") {
                            return ["LOGIN_QR", "LOGIN_PIN", "QR", "PIN"].includes(loginTypeItem.code);
                          }
                          // 그 외에는 모두 표시
                          return true;
                        })
                        .map((loginTypeItem: any) => {
                          let displayName = loginTypeItem.name;
                          if (loginTypeItem.code === "LOGIN_QR" || loginTypeItem.code === "QR") displayName = "QR 코드";
                          else if (loginTypeItem.code === "LOGIN_PIN" || loginTypeItem.code === "PIN") displayName = "PIN 코드";

                          return (
                            <MenuItem key={loginTypeItem.code} value={loginTypeItem.code} sx={{ fontSize: "0.875rem" }}>
                              {displayName} ({loginTypeItem.code})
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </MDBox>
                {/* PIN이 선택된 경우 생성된 PIN 표시 */}
                {(loginType === "PIN" || loginType === "LOGIN_PIN") && this.state.generatedPin && (
                  <Card
                    elevation={0}
                    sx={{
                      mb: 2,
                      p: 3,
                      backgroundColor: backgroundColor,
                      borderRadius: 1,
                      border: `2px solid ${theme?.palette?.info?.main || "#1976d2"}`,
                      boxShadow: "none",
                    }}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      mb={2}
                      display="block"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: "medium",
                        color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
                      }}>
                      생성된 PIN 번호
                    </MDTypography>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <MDTypography
                        variant="h3"
                        fontWeight="bold"
                        color="info"
                        sx={{
                          fontFamily: "monospace",
                          letterSpacing: 4,
                          fontSize: "3rem",
                          lineHeight: 1.2,
                        }}>
                        {this.state.generatedPin}
                      </MDTypography>
                      <MDButton variant="outlined" color="info" size="medium" onClick={this.generatePin} sx={{ ml: 2, minWidth: 100 }}>
                        재생성
                      </MDButton>
                    </MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{
                        fontSize: "0.75rem",
                        textAlign: "center",
                        color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
                      }}>
                      사용자에게 이 PIN 번호를 전달해주세요.
                    </MDTypography>
                  </Card>
                )}
              </>
            )}
            <MDBox mb={2}>
              <MDInput label="이름" fullWidth value={name} onChange={(e: any) => this.handleChange("name", e.target.value)} />
            </MDBox>
            <MDBox mb={2}>
              <MDInput label="이메일" type="email" fullWidth value={email} onChange={(e: any) => this.handleChange("email", e.target.value)} />
            </MDBox>
            <MDBox mb={2}>
              <MDInput label="전화번호" fullWidth value={mobile} onChange={(e: any) => this.handleChange("mobile", e.target.value)} />
            </MDBox>


            {/* Policy Selection Section - Only for USER role */}
            {tenantRole === "USER" && (
              <MDBox mb={3} mt={3}>
                <MDTypography variant="h6" mb={2} color={finalDarkMode ? "white" : "dark"}>
                  정책 설정
                </MDTypography>



                {/* 걸음수 포인트 적립 정책 */}
                <MDBox mb={2}>
                  <Autocomplete
                    multiple
                    options={this.state.stepPolicies}
                    getOptionLabel={(option: any) => option.policyName || ""}
                    value={this.state.stepPolicies.filter((p: any) => this.state.stepPolicySeqIds.includes(p.seqId))}
                    onChange={(_event: any, newValue: any[]) => {
                      this.setState({ stepPolicySeqIds: newValue.map((v: any) => v.seqId) })
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.policyName}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                    loading={this.state.loadingPolicies}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="걸음수 포인트 적립 정책 (다중 선택)"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: finalDarkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                            "& fieldset": {
                              borderColor: finalDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                          },
                          "& .MuiInputBase-input": {
                            color: finalDarkMode ? "#fff" : "#000",
                          },
                        }}
                      />
                    )}
                  />
                </MDBox>

                {/* 영상시청 포인트 적립 정책 */}
                <MDBox mb={2}>
                  <Autocomplete
                    multiple
                    options={this.state.videoPolicies}
                    getOptionLabel={(option: any) => option.policyName || ""}
                    value={this.state.videoPolicies.filter((p: any) => this.state.videoPolicySeqIds.includes(p.seqId))}
                    onChange={(_event: any, newValue: any[]) => {
                      this.setState({ videoPolicySeqIds: newValue.map((v: any) => v.seqId) })
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.policyName}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                    loading={this.state.loadingPolicies}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="영상시청 포인트 적립 정책 (다중 선택)"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: finalDarkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                            "& fieldset": {
                              borderColor: finalDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                          },
                          "& .MuiInputBase-input": {
                            color: finalDarkMode ? "#fff" : "#000",
                          },
                        }}
                      />
                    )}
                  />
                </MDBox>

                {/* 챌린지 정책 (월간 챌린지 등) */}
                <MDBox mb={2}>
                  <Autocomplete
                    multiple
                    options={this.state.challengePolicies}
                    getOptionLabel={(option: any) => option.policyName || ""}
                    value={this.state.challengePolicies.filter((p: any) => this.state.challengePolicySeqIds.includes(p.seqId))}
                    onChange={(_event: any, newValue: any[]) => {
                      this.setState({ challengePolicySeqIds: newValue.map((v: any) => v.seqId) })
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.policyName}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                    loading={this.state.loadingPolicies}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="챌린지 정책 (다중 선택)"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: finalDarkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                            "& fieldset": {
                              borderColor: finalDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                          },
                          "& .MuiInputBase-input": {
                            color: finalDarkMode ? "#fff" : "#000",
                          },
                        }}
                      />
                    )}
                  />
                </MDBox>

                {/* 포인트 한도 정책 */}
                <MDBox mb={2}>
                  <Autocomplete
                    multiple
                    options={this.state.pointLimitPolicies}
                    getOptionLabel={(option: any) => option.policyName || ""}
                    value={this.state.pointLimitPolicies.filter((p: any) => this.state.pointLimitPolicySeqIds.includes(p.seqId))}
                    onChange={(_event: any, newValue: any[]) => {
                      this.setState({ pointLimitPolicySeqIds: newValue.map((v: any) => v.seqId) })
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.policyName}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                    loading={this.state.loadingPolicies}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="포인트 한도 정책 (다중 선택)"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: finalDarkMode ? "rgba(255, 255, 255, 0.05)" : "transparent",
                            "& fieldset": {
                              borderColor: finalDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.23)",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                          },
                          "& .MuiInputBase-input": {
                            color: finalDarkMode ? "#fff" : "#000",
                          },
                        }}
                      />
                    )}
                  />
                </MDBox>

                <MDBox mb={1}>
                  <MDTypography variant="subtitle2" fontWeight="medium" color={finalDarkMode ? "white" : "dark"}>
                    기본 적용 정책 (자동 할당)
                  </MDTypography>
                </MDBox>
                <MDBox mb={3} p={2} bgColor={finalDarkMode ? "rgba(255, 255, 255, 0.05)" : "grey-100"} borderRadius="lg">
                  {this.state.allDefaultPolicies.length > 0 ? (
                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                      {this.state.allDefaultPolicies.map((policy, index) => (
                        <Chip
                          key={`default-policy-${index}`}
                          label={policy.policyName}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ))}
                    </MDBox>
                  ) : (
                    <MDTypography variant="caption" color="text">
                      자동 할당된 기본 정책이 없습니다.
                    </MDTypography>
                  )}
                </MDBox>




                <MDBox mb={1}>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    관심 치료 부위 (다중 선택)
                  </MDTypography>
                </MDBox>
                <MDBox mb={4}>
                  <Autocomplete
                    multiple
                    options={this.state.availableBodyParts}
                    groupBy={(option) => {
                      const cat = this.state.interestAreaCategories.find(c => c.code === option.category);
                      return cat ? cat.name : "기타";
                    }}
                    getOptionLabel={(option) => option.name}
                    value={this.state.availableBodyParts.filter(p => this.state.interestBodyPartIds.includes(p.seqId))}
                    onChange={(event, newValue) => {
                      this.setState({ interestBodyPartIds: newValue.map(v => v.seqId) });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.name}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="관심 부위 선택" variant="outlined" />
                    )}
                  />
                </MDBox>
                <MDBox mb={1}>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    수술 부위 등록
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                  <Autocomplete
                    options={this.state.availableSurgerySites}
                    getOptionLabel={(option) => option.siteName}
                    value={this.state.selectedSurgerySite}
                    onChange={(event, newValue) => {
                      this.setState({ selectedSurgerySite: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="수술 부위 선택" variant="outlined" fullWidth />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="수술 날짜"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={
                      this.state.selectedSurgeryDate
                        ? this.state.selectedSurgeryDate.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const date = e.target.value ? new Date(e.target.value) : null;
                      this.setState({ selectedSurgeryDate: date });
                    }}
                    sx={{
                      width: 180,
                      "& input::-webkit-calendar-picker-indicator": {
                        display: "block",
                        filter: finalDarkMode ? "invert(1)" : "none",
                        cursor: "pointer",
                      },
                    }}
                  />
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={this.handleAddSurgerySite}
                    disabled={!this.state.selectedSurgerySite}
                  >
                    추가
                  </MDButton>
                </MDBox>
                <MDBox display="flex" flexWrap="wrap" gap={1}>
                  {this.state.liveSurgerySites.map((site, index) => (
                    <Chip
                      key={index}
                      label={`${site.siteName} (${site.date ? site.date.toISOString().split("T")[0] : "날짜 없음"})`}
                      onDelete={() => this.handleRemoveSurgerySite(index)}
                      sx={{
                        backgroundColor: finalDarkMode ? "rgba(255, 255, 255, 0.1)" : "#e0e0e0",
                        color: finalDarkMode ? "#fff" : "#000",
                      }}
                    />
                  ))}
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: backgroundColor,
            padding: "16px 24px 24px 24px", // 하단 padding 증가
          }}>
          <MDButton variant="text" color="secondary" onClick={onClose} disabled={loading}>
            취소
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={this.handleSubmit} disabled={loading}>
            {loading ? "등록 중..." : "등록"}
          </MDButton>
        </DialogActions>
      </Dialog>
    )
  }
}

function AddUserDialog(props: AddUserDialogProps) {
  const [controller] = useMaterialUIController()
  const theme = useTheme()
  const { darkMode } = controller
  // Material-UI 테마에서 직접 다크 모드 확인 (fallback)
  const isDarkMode = darkMode ?? theme.palette.mode === "dark"

  // Dialog가 테마를 제대로 따르도록 ThemeProvider로 감싸기
  // key prop을 사용하여 darkMode 변경 시 강제 리렌더링
  return (
    <ThemeProvider theme={theme}>
      <AddUserDialogClass {...props} darkMode={isDarkMode} theme={theme} key={`add-user-dialog-${isDarkMode}`} />
    </ThemeProvider>
  )
}

export default AddUserDialog
